// src/ui/screens/NewContactScreen.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { userSearchService } from '../../services/contact/userSearchService';
import { countries } from '../../utils/countries';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
};

// ── Lookup State ─────────────────────────────────────────────────────────────

const LOOKUP_STATE = {
  IDLE: 'idle',
  FOUND: 'found',
  DUPLICATE: 'duplicate',
  NOT_FOUND: 'notFound',
};

// ── NewContactScreen ─────────────────────────────────────────────────────────

export const NewContactScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [code, setCode] = useState('+');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showDivider, setShowDivider] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [syncToPhone, setSyncToPhone] = useState(true);
  const [lookupState, setLookupState] = useState(LOOKUP_STATE.IDLE);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const codeInputRef = useRef(null);
  const numberInputRef = useRef(null);
  const firstNameInputRef = useRef(null);
  const debounceRef = useRef(null);

  // ── Computed ──────────────────────────────────────────────────────────────

  const getFullPhone = () => {
    if (!selectedCountry) return '';
    const raw = phoneNumber.trim();
    let normalized = raw.replace(/\D/g, '');
    if (normalized.startsWith('0')) {
      normalized = normalized.substring(1);
    }
    return `+${selectedCountry.dialCode}${normalized}`;
  };

  const canSave = firstName.trim().length > 0 &&
    selectedCountry !== null &&
    phoneNumber.trim().length >= 4 &&
    !isSaving;

  // ── Country detection ─────────────────────────────────────────────────────

  const detectCountryFromCode = (value) => {
    if (isAutoFilling) return;

    if (!value.startsWith('+')) {
      setCode(`+${value}`);
      return;
    }

    const codeWithoutPlus = value.substring(1);

    for (const country of countries) {
      if (country.dialCode === codeWithoutPlus) {
        setSelectedCountry(country);
        setShowDivider(true);
        setLookupState(LOOKUP_STATE.IDLE);

        if (!isAutoFilling && codeWithoutPlus.length > 0) {
          setIsAutoFilling(true);
          setTimeout(() => {
            numberInputRef.current?.focus();
            setIsAutoFilling(false);
          }, 100);
        }
        break;
      }
    }
  };

  const handleCodeFieldClick = () => {
    if (!code || code === '') {
      setCode('+');
    } else if (!code.startsWith('+')) {
      setCode(`+${code}`);
    }
  };

  // ── Lookup user ────────────────────────────────────────────────────────────

  const lookupUser = async () => {
    const phone = getFullPhone();
    if (!phone) return;

    try {
      // Check if already exists in device contacts
      // In browser, we'll skip this check since we can't access contacts
      
      // Check Filatom users
      const result = await userSearchService.searchUsersByPhones({
        phoneNumbersA: [phone],
        token: user?.uid || '',
      });

      if (result && result[phone]) {
        setLookupState(LOOKUP_STATE.FOUND);
      } else {
        setLookupState(LOOKUP_STATE.NOT_FOUND);
      }
    } catch (error) {
      console.error('Lookup error:', error);
      setLookupState(LOOKUP_STATE.IDLE);
    }
  };

  // ── Phone number change handler ───────────────────────────────────────────

  const handlePhoneNumberChange = (value) => {
    setPhoneNumber(value);
    setLookupState(LOOKUP_STATE.IDLE);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (selectedCountry && value.trim().length >= 4) {
      debounceRef.current = setTimeout(() => {
        lookupUser();
      }, 600);
    }
  };

  // ── Pick country ──────────────────────────────────────────────────────────

  const handlePickCountry = () => {
    navigate('/country-picker', {
      state: { returnTo: '/new-contact' }
    });
  };

  // ── Invite contact ────────────────────────────────────────────────────────

  const inviteContact = () => {
    const message = encodeURIComponent(
      "Hey, Let's chat on Filatom! Download now."
    );
    window.location.href = `sms:${getFullPhone()}?body=${message}`;
  };

  // ── Save contact ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!canSave || isSaving) return;

    setIsSaving(true);

    try {
      // In a real implementation, this would save to device contacts
      // For web, we'll just simulate the save
      
      // Show success message
      alert(`${firstName.trim()} saved successfully`);

      navigate(-1);
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save contact');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Cleanup ────────────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // ── Focus on mount ────────────────────────────────────────────────────────

  useEffect(() => {
    setTimeout(() => {
      codeInputRef.current?.focus();
    }, 100);
  }, []);

  // ── Render status line ────────────────────────────────────────────────────

  const renderStatusLine = () => {
    if (lookupState === LOOKUP_STATE.IDLE) return null;

    switch (lookupState) {
      case LOOKUP_STATE.FOUND:
        return (
          <div className="flex items-center gap-1 mt-1.5">
            <span className="text-white/50 text-xs">Phone number is on Filatom</span>
            <span className="text-green-500 text-xs">✓</span>
          </div>
        );
      case LOOKUP_STATE.DUPLICATE:
        return (
          <div className="flex items-center gap-1 mt-1.5">
            <span className="text-white/50 text-xs">Phone number already exists in contacts</span>
            <span className="text-green-500 text-xs">✓</span>
          </div>
        );
      case LOOKUP_STATE.NOT_FOUND:
        return (
          <div className="flex items-center gap-1 mt-1.5">
            <span className="text-white/50 text-xs">Phone number is not on Filatom.</span>
            <button onClick={inviteContact} className="text-filatom-primary text-xs font-medium">
              Invite
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* App Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-filatom-bg">
        <button
          onClick={() => navigate(-1)}
          className="text-white text-sm font-normal"
        >
          Cancel
        </button>
        <span className="text-white text-lg font-semibold">New contact</span>
        <button
          onClick={handleSave}
          disabled={!canSave || isSaving}
          className={`text-sm font-medium ${
            canSave && !isSaving ? 'text-filatom-primary' : 'text-white/40'
          }`}
        >
          {isSaving ? (
            <div className="w-4.5 h-4.5 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            'Save'
          )}
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pb-8">
        {/* Name Card */}
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: COLORS.card }}>
          <input
            ref={firstNameInputRef}
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            className="w-full bg-transparent text-white text-sm outline-none px-4 py-3.5 placeholder:text-white/40"
          />
          <div className="h-px bg-white/10" />
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            className="w-full bg-transparent text-white text-sm outline-none px-4 py-3.5 placeholder:text-white/40"
          />
        </div>

        <div className="h-5" />

        {/* Label */}
        <p className="text-white/50 text-xs ml-1 mb-2">Add phone number</p>

        {/* Phone Card */}
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: COLORS.card }}>
          <button
            onClick={handlePickCountry}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-filatom-card-hover transition-colors"
          >
            <span className={selectedCountry ? 'text-white text-sm font-medium' : 'text-white/40 text-sm'}>
              {selectedCountry ? `${selectedCountry.flag} ${selectedCountry.name}` : 'Country'}
            </span>
            <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="h-px bg-white/10 mx-4" />

          <div className="flex items-center px-4 py-2">
            <div className="w-[68px] flex-shrink-0">
              <input
                ref={codeInputRef}
                type="text"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  detectCountryFromCode(e.target.value);
                }}
                onClick={handleCodeFieldClick}
                className="w-full bg-transparent text-white text-sm outline-none"
                placeholder="+"
              />
            </div>

            {showDivider && (
              <div className="w-px h-5 bg-filatom-primary mx-2 flex-shrink-0" />
            )}

            <div className="flex-1">
              <input
                ref={numberInputRef}
                type="tel"
                value={phoneNumber}
                onChange={(e) => handlePhoneNumberChange(e.target.value)}
                placeholder={showDivider ? 'Phone number' : ''}
                disabled={!showDivider}
                className="w-full bg-transparent text-white text-sm outline-none placeholder:text-white/30"
              />
            </div>
          </div>
        </div>

        {/* Status Line */}
        {renderStatusLine()}

        <div className="h-5" />

        {/* Sync Card */}
        <div
          className="rounded-xl flex items-center justify-between px-4 py-3"
          style={{ backgroundColor: COLORS.card }}
        >
          <div>
            <p className="text-white text-sm">Sync contact to phone</p>
            <p className="text-white/50 text-xs mt-0.5">Automatically save contacts to your device</p>
          </div>
          <button
            onClick={() => setSyncToPhone(!syncToPhone)}
            className={`w-12 h-6 rounded-full transition-all relative ${
              syncToPhone ? 'bg-filatom-primary' : 'bg-gray-600'
            }`}
          >
            <div
              className={`w-5 h-5 rounded-full bg-white transition-all absolute top-0.5 ${
                syncToPhone ? 'right-0.5' : 'left-0.5'
              }`}
            />
          </button>
        </div>

        <div className="h-7" />

        {/* QR Code */}
        <button className="flex items-center justify-center gap-2 w-full">
          <svg className="w-7 h-7 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
          <span className="text-filatom-primary text-sm font-medium">Add via QR code</span>
        </button>
      </div>
    </div>
  );
};

export default NewContactScreen;